import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiShield,
  FiArrowLeft,
  FiEdit3,
  FiCheckCircle,
  FiHash,
  FiCalendar,
} from "react-icons/fi";

import Api from "../../components/api/Api";
import styles from "./UserItem.module.css";

const extractCustomerData = (data) => {
  if (!data) return null;
  if (data.customerDetail) return data.customerDetail;
  if (data.customerDetails) return data.customerDetails;
  if (data.customer) return data.customer;
  if (data.customerData) return data.customerData;
  if (
    data.first_name ||
    data.last_name ||
    data.mobile_number ||
    data.email_id ||
    data.email_address
  )
    return data;
  if (
    data.data &&
    (data.data.first_name || data.data.email_id || data.data.mobile_number)
  )
    return data.data;
  if (
    data.user &&
    (data.user.first_name || data.user.email_address || data.user.email_id)
  )
    return data.user;
  return data;
};

const UserItem = () => {
  const { userId } = useParams();
  const [userDetail, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const getDetailsApi = Api({
    url: `admin/user/id/${userId}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let details = null;
        try {
          const res = await axios.get(
            `${baseUrl}/customer/customerDetail/${userId}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          details = extractCustomerData(res.data);
        } catch (err) {
          const responseData = await getDetailsApi.fetchData();
          details = extractCustomerData(responseData?.data);
        }
        setUserDetails(details);
      } catch (error) {
        console.error("Error fetching customer details:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token, baseUrl]);

  const firstName =
    userDetail?.first_name || userDetail?.user?.first_name || "";
  const lastName =
    userDetail?.last_name || userDetail?.user?.last_name || "";
  const fullName =
    [firstName, lastName].filter(Boolean).join(" ") ||
    userDetail?.name ||
    userDetail?.user?.name ||
    "User Profile";

  const mobileNumber =
    userDetail?.mobile_number ||
    userDetail?.mobile_no ||
    userDetail?.phone ||
    userDetail?.user?.mobile_number ||
    "N/A";
  const emailAddress =
    userDetail?.email_address ||
    userDetail?.email_id ||
    userDetail?.email ||
    userDetail?.user?.email_address ||
    userDetail?.user?.email_id ||
    "N/A";
  const userTypeDisplay =
    userDetail?.userType?.name ||
    userDetail?.user_type ||
    (typeof userDetail?.userType === "string"
      ? userDetail?.userType
      : "CUSTOMER");

  const photo =
    userDetail?.image_url ||
    userDetail?.profile_photo ||
    userDetail?.photo ||
    userDetail?.user?.photo;
  const rawAssetUrl =
    userDetail?.assetUrl ||
    loggedInUser?.assetUrl ||
    process.env.REACT_APP_ASSET_URL ||
    "https://images.allhomedesire.in";
  const cleanAssetUrl = rawAssetUrl.replace(/\/+$/, "");
  const profilePhotoSrc = photo
    ? photo.startsWith("http://") || photo.startsWith("https://")
      ? photo
      : `${cleanAssetUrl}${photo.startsWith("/") ? "" : "/"}${photo}`
    : "https://plus.unsplash.com/premium_photo-1711393509013-779e93b0da90?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxlZGl0b3JpYWwtZmVlZHwzMnx8fGVufDB8fHx8fA%3D%3D";

  const createdOn = userDetail?.created_on || userDetail?.created_at || "N/A";

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.spinnerContainer}>
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading details…</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Unified Single Profile Card */}
          <div className={styles.profileCard}>
            {/* Banner & Hero Header */}
            <div className={styles.banner}>
              <div className={styles.bannerPattern} />
            </div>
            <div className={styles.headerBody}>
              <div className={styles.userMainInfo}>
                <div className={styles.avatarWrapper}>
                  <img
                    src={profilePhotoSrc}
                    alt={fullName}
                    className={styles.avatar}
                    onError={(e) => {
                      e.target.src =
                        "https://plus.unsplash.com/premium_photo-1711393509013-779e93b0da90?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3";
                    }}
                  />
                  <div
                    className={styles.statusBadge}
                    title="Active User Account"
                  />
                </div>
                <div className={styles.identityBlock}>
                  <h1 className={styles.userName}>{fullName}</h1>
                  <div className={styles.metaRow}>
                    <span className={styles.userTypeBadge}>
                      <FiShield size={13} />
                      {userTypeDisplay}
                    </span>
                    <span className={styles.userIdChip}>
                      ID: #{userId || userDetail?.id || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.headerActions}>
                <Link to={`/user/${userId}/edit`} className={styles.btnEdit}>
                  <FiEdit3 size={16} />
                  Edit Profile
                </Link>
                <Link to="/user" className={styles.btnBack}>
                  <FiArrowLeft size={16} />
                  Back
                </Link>
              </div>
            </div>

            {/* Unified Inner Sections */}
            <div className={styles.cardContentBody}>
              <div className={styles.detailsGrid}>
                {/* Personal Information */}
                <div className={styles.sectionBlock}>
                  <div className={styles.sectionHeader}>
                    <FiUser className={styles.sectionIcon} />
                    Personal Information
                  </div>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>First Name</span>
                      <div className={styles.infoValue}>
                        <FiUser className={styles.infoValueIcon} />
                        {firstName || "N/A"}
                      </div>
                    </div>

                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>Last Name</span>
                      <div className={styles.infoValue}>
                        <FiUser className={styles.infoValueIcon} />
                        {lastName || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className={styles.sectionBlock}>
                  <div className={styles.sectionHeader}>
                    <FiMail className={styles.sectionIcon} />
                    Contact Details
                  </div>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>Mobile Number</span>
                      <div className={styles.infoValue}>
                        <FiPhone className={styles.infoValueIcon} />
                        {mobileNumber}
                      </div>
                    </div>

                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>Email Address</span>
                      <div className={styles.infoValue}>
                        <FiMail className={styles.infoValueIcon} />
                        {emailAddress}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Overview */}
              <div className={styles.sectionBlockFull}>
                <div className={styles.sectionHeader}>
                  <FiShield className={styles.sectionIcon} />
                  Account Overview
                </div>
                <div className={styles.infoGridFour}>
                  <div className={styles.infoBlock}>
                    <span className={styles.infoLabel}>User Role / Type</span>
                    <div className={styles.infoValue}>
                      <FiShield className={styles.infoValueIcon} />
                      {userTypeDisplay}
                    </div>
                  </div>

                  <div className={styles.infoBlock}>
                    <span className={styles.infoLabel}>Account Status</span>
                    <div className={styles.infoValue} style={{ color: "#059669" }}>
                      <FiCheckCircle
                        className={styles.infoValueIcon}
                        style={{ color: "#10b981" }}
                      />
                      Active
                    </div>
                  </div>

                  <div className={styles.infoBlock}>
                    <span className={styles.infoLabel}>User ID</span>
                    <div className={styles.infoValue}>
                      <FiHash className={styles.infoValueIcon} />
                      {userId || userDetail?.id || "N/A"}
                    </div>
                  </div>

                  <div className={styles.infoBlock}>
                    <span className={styles.infoLabel}>Member Since</span>
                    <div className={styles.infoValue}>
                      <FiCalendar className={styles.infoValueIcon} />
                      {createdOn}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserItem;
